// Transaction Analysis: month buckets, category totals and the chart palette.
// Transfers are left out everywhere (they move money between your own wallets,
// e.g. paying a credit-card bill, so counting them would double-count spending).

// Categorical palette, validated with the dataviz skill's validate_palette.js on the
// app's white surface (light mode; the app's dark mode is disabled):
// adjacent CVD ΔE ≥ 9.1, normal-vision ΔE ≥ 19.6. Slots 3–5 are below 3:1 contrast,
// so every chart on the page also has visible labels / a table with the numbers.
// Order matters (it is what keeps neighbours apart): never re-order or cycle.
export const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300'];
export const OTHER_COLOR = '#a8a29e'; // "Other" (folded tail) - deliberately gray
export const INCOME_COLOR = SERIES_COLORS[0];
export const EXPENSE_COLOR = SERIES_COLORS[1];
export const TOP_CATEGORIES = SERIES_COLORS.length;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n) => String(n).padStart(2, '0');
export const monthKey = (year, monthIndex) => `${year}-${pad(monthIndex + 1)}`;

// Period → list of months { key, year, month, label } and the date range to fetch
export const periodMonths = (period, today = new Date()) => {
  let start;
  let end;
  if (period === 'last12') {
    end = new Date(today.getFullYear(), today.getMonth(), 1);
    start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
  } else if (period === 'last6') {
    end = new Date(today.getFullYear(), today.getMonth(), 1);
    start = new Date(end.getFullYear(), end.getMonth() - 5, 1);
  } else {
    const year = Number(period);
    start = new Date(year, 0, 1);
    // The current year stops at this month
    end = year === today.getFullYear() ? new Date(year, today.getMonth(), 1) : new Date(year, 11, 1);
  }
  const months = [];
  for (let d = new Date(start); d <= end; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
    months.push({ key: monthKey(d.getFullYear(), d.getMonth()), year: d.getFullYear(), month: d.getMonth() });
  }
  const spansYears = months.length && months[0].year !== months[months.length - 1].year;
  months.forEach((m) => {
    m.label = spansYears ? `${MONTHS[m.month]} '${String(m.year).slice(2)}` : MONTHS[m.month];
    m.longLabel = `${MONTHS[m.month]} ${m.year}`;
  });
  const lastDay = new Date(end.getFullYear(), end.getMonth() + 1, 0);
  return {
    months,
    from: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-01`,
    to: `${lastDay.getFullYear()}-${pad(lastDay.getMonth() + 1)}-${pad(lastDay.getDate())}`,
  };
};

// Money in (income) and money out (expense) for one transaction, as positive numbers.
// A positive "expense" (a refund) reduces spending; a negative "income" reduces income.
export const flowOf = (tx) => {
  const amount = Number(tx.amount) || 0;
  if (tx.type === 'income') return { income: amount, expense: 0 };
  if (tx.type === 'expense') return { income: 0, expense: -amount };
  return { income: 0, expense: 0 };
};

export const UNCATEGORISED = 'Uncategorised';
export const categoryName = (tx) => tx.category?.name || UNCATEGORISED;

// Everything the page shows, from one list of transactions
export const analyse = (transactions, months, type) => {
  const byMonth = Object.fromEntries(months.map(m => [m.key, { income: 0, expense: 0, categories: {} }]));
  const categoryTotals = {};
  let income = 0;
  let expense = 0;

  transactions.forEach((tx) => {
    if (tx.type !== 'income' && tx.type !== 'expense') return;
    const bucket = byMonth[String(tx.date).slice(0, 7)];
    if (!bucket) return;
    const flow = flowOf(tx);
    bucket.income += flow.income;
    bucket.expense += flow.expense;
    income += flow.income;
    expense += flow.expense;
    if (tx.type === type) {
      const name = categoryName(tx);
      const value = type === 'income' ? flow.income : flow.expense;
      bucket.categories[name] = (bucket.categories[name] || 0) + value;
      categoryTotals[name] = (categoryTotals[name] || 0) + value;
    }
  });

  const ranked = Object.entries(categoryTotals)
    .map(([name, total]) => ({ name, total }))
    .filter(c => Math.abs(c.total) > 0.004)
    .sort((a, b) => b.total - a.total);
  const typeTotal = ranked.reduce((sum, c) => sum + c.total, 0);
  ranked.forEach((c) => { c.share = typeTotal > 0 ? c.total / typeTotal : 0; });

  const rows = months.map((m) => {
    const b = byMonth[m.key];
    const top = Object.entries(b.categories).sort((x, y) => y[1] - x[1])[0];
    return {
      ...m,
      income: b.income,
      expense: b.expense,
      net: b.income - b.expense,
      rate: b.income > 0 ? (b.income - b.expense) / b.income : null,
      categories: b.categories,
      topCategory: top ? top[0] : null,
    };
  });

  // Months that have happened (for the monthly average)
  const activeMonths = rows.filter(r => r.income || r.expense).length || 1;

  return {
    rows,
    ranked,
    totals: {
      income,
      expense,
      net: income - expense,
      rate: income > 0 ? (income - expense) / income : null,
      avgExpense: expense / activeMonths,
      avgIncome: income / activeMonths,
      activeMonths,
    },
  };
};

// Colour slots by the category's rank (largest first). The caller ranks over the
// whole period for all wallets, so changing the wallet filter never repaints a category.
export const assignColors = (rankedNames) => {
  const colors = {};
  rankedNames.slice(0, TOP_CATEGORIES).forEach((name, i) => { colors[name] = SERIES_COLORS[i]; });
  return colors;
};

// Axis ticks: ₹45k, ₹1.2L, ₹1.5Cr
export const compactRupees = (value) => {
  const v = Number(value) || 0;
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  const trim = (n) => String(Number(n.toFixed(1)));
  if (abs >= 1e7) return `${sign}₹${trim(abs / 1e7)}Cr`;
  if (abs >= 1e5) return `${sign}₹${trim(abs / 1e5)}L`;
  if (abs >= 1e3) return `${sign}₹${trim(abs / 1e3)}k`;
  return `${sign}₹${Math.round(abs)}`;
};

export const formatRupees = (amount, digits = 0) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: digits, maximumFractionDigits: digits })
    .format(Number(amount) || 0);

export const formatPercent = (ratio) => {
  if (ratio === null || ratio === undefined || !isFinite(ratio)) return '—';
  if (ratio > 0 && ratio < 0.005) return '<1%'; // never show a real share as 0%
  return `${Math.round(ratio * 100)}%`;
};
